import { useQuery } from "@tanstack/react-query";
import type { TournamentRole } from "@alpha/shared";
import { api, ApiError } from "./api";
import { useAuth } from "./auth";
import type { Profile, Session } from "./types";

/** The query key, exported so tests and mutations can prime or drop it. */
export const SESSION_KEY = ["me-session"] as const;

/** Kept as `["profile"]`: the wizard invalidates this key by name. */
export const PROFILE_KEY = ["profile"] as const;

/**
 * Who the caller is, on both axes the server answers: the global tier —
 * anonymous, coach, or admin — plus any roles granted on individual
 * tournaments. The second axis crosses the first rather than extending it, so
 * a coach can hold `tournamentAdmin` on one event and nothing anywhere else;
 * read `isAdmin` and `tournamentRoles` as separate questions.
 *
 * One cache entry serves every consumer, so gating more of the UI on role costs
 * no extra requests.
 *
 * `staleTime` matches the 60s allowlist cache on the server: refetching sooner
 * could not return a fresher answer.
 */
export function useSession() {
  const { idToken } = useAuth();
  return useQuery({
    queryKey: SESSION_KEY,
    enabled: !!idToken,
    staleTime: 60_000,
    retry: false,
    queryFn: () => api.get<Session>("/me/session"),
  });
}

/**
 * Admin-ness alone, false while the answer is still in flight. Callers use this
 * to decide whether to *offer* something; the server decides whether to allow
 * it, so an optimistic false costs nothing but a link that appears a beat late.
 */
export function useIsAdmin(): boolean {
  return useSession().data?.isAdmin ?? false;
}

/**
 * What the caller was granted on one tournament, or null. Deliberately not an
 * answer to "may they do this": a global admin outranks every scoped grant and
 * holds none, so call sites combine this with `useIsAdmin()` rather than
 * treating null as "no access".
 *
 * `tid` is the route param, which is `""` before a route resolves — an empty
 * id can match no grant, so it answers null rather than the first row.
 */
export function useTournamentRole(tid: string | undefined): TournamentRole | null {
  const roles = useSession().data?.tournamentRoles ?? [];
  if (!tid) return null;
  return roles.find((r) => r.tournamentId === tid)?.role ?? null;
}

/**
 * Does the caller hold a grant on any tournament at all? The console gate asks
 * this and nothing narrower: whether someone belongs inside /admin is a
 * question about the console, and by the time a tournament id exists they are
 * already past it.
 */
export function useHasAnyTournamentRole(): boolean {
  return (useSession().data?.tournamentRoles ?? []).length > 0;
}

/**
 * The account holder's own details — the only place a coach's name is stored,
 * since Cognito keeps nothing but the email.
 *
 * A profile does not exist until the wizard has been through once, so `null`
 * means "signed in, not set up yet" and `undefined` means "still asking". Every
 * consumer has to tell those apart, which is why the 404 is caught here rather
 * than at each call site: a version that threw instead would put the shared
 * cache entry into an error state and the dashboard would stop offering the
 * wizard to the very people who need it.
 */
export function useProfile() {
  const { idToken } = useAuth();
  return useQuery({
    queryKey: PROFILE_KEY,
    enabled: !!idToken,
    retry: false,
    queryFn: async () => {
      try {
        return await api.get<Profile>("/me/profile");
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });
}
